import { InjectionToken } from "@angular/core";
import { CorrectionParameters, ParameterSetRequest, SupervisionParameters } from "./models/parameter";
import { StaffMemberFilter } from "./models/staff-member";
import { SurveyFilter } from "./models/survey";

export const SKIP = 0;
export const LIMIT = 50;
export const PRIORITY_LB = 1;
export const PRIORITY_UB = 5;
export const POLL_INTERVAL = 5000;
export const NG_PRIMARY = '#3F51B5';
export const NG_ACCENT = '#FF4081';

export const NO_AUTH_ROUTES = ['login', 'register', 'refresh'];

export const PREFERENCE_TYPES = new InjectionToken<string[]>('PREFERENCE_TYPES', {
  providedIn: 'root',
  factory: () => ['Language', 'Location', 'Temporal', 'Field']
});

export const PREFERENCE_PRIORITIES = new InjectionToken<number[]>('PREFERENCE_PRIORITIES', {
  providedIn: 'root',
  factory: () => {
    const NUM_PRIORITIES = PRIORITY_UB - PRIORITY_LB + 1;
    return [...Array(NUM_PRIORITIES).keys()].map(x => x + PRIORITY_LB);
  }
});

export const DATE_FORMAT = new InjectionToken<string>('DATE_FORMAT', {
  providedIn: 'root',
  factory: () => 'dd.MM.yyyy'
});

export const TIME_FORMAT = new InjectionToken<string>('TIME_FORMAT', {
  providedIn: 'root',
  factory: () => 'HH:mm'
});

export const DATE_TIME_FORMAT = new InjectionToken<string>('DATE_TIME_FORMAT', {
  providedIn: 'root',
  factory: () => 'dd.MM.yyyy, HH:mm'
});

export const SURVEY_DEFAULT_FILTER: SurveyFilter = {
  name: null,
  begin: null,
  end: null,
  isActive: null
};

export const EXAM_DEFAULT_FILTER = {
  moduleCode: null,
  moduleName: null,
  begin: null,
  end: null,
  examTypeId: null,
  participantsFrom: null,
  participantsTo: null,
  isRepetition: null
};

export const STAFF_MEMBER_DEFAULT_FILTER: StaffMemberFilter = {
  lastName: null,
  firstName: null,
  email: null,
  requiredSupervisionsFrom: null,
  requiredSupervisionsTo: null,
  requiredCorrectionsFrom: null,
  requiredCorrectionsTo: null,
  supervisionsFrom: null,
  supervisionsTo: null,
  correctionsFrom: null,
  correctionsTo: null
};

export const DEFAULT_CORRECTS = {
  count: 0,
  data: []
};

export const SUPERVISION_DEFAULT: SupervisionParameters = {
  time_limit_minutes: 2,
  gap: 0,
  experience: 0,
  involvement: 30,
  language: 0,
  affiliation: 10,
  location: 0,
  holiday: 100,
  violations: 100,
  exam_phase: 0,
  workload: 0,
  admin_assignment: 40,
  admin_exclusion: 100,
  retake_exam_only: 20
};

export const CORRECTION_DEFAULT: CorrectionParameters = {
  time_limit_minutes: 5,
  gap: 0,
  experience: 0,
  involvement: 30,
  language: 0,
  affiliation: 10,
  teaching_assistant: 0,
  violations: 0,
  exam_phase: 0,
  workload: 25,
  spread: 100,
  field: 0
};

export const PARAMETER_TOOLTIPS: Map<string, string> = new Map;
PARAMETER_TOOLTIPS.set('time_limit_minutes', 'Solver time limit in minutes');
PARAMETER_TOOLTIPS.set('gap', 'Optimality gap (multiplied by 10^-4)');
PARAMETER_TOOLTIPS.set('experience', 'Prefer exam-detail of staff with longer employment');
PARAMETER_TOOLTIPS.set('involvement', 'Prefer exam-detail of the module\'s TAs/tutors');
PARAMETER_TOOLTIPS.set('language', 'Reward fulfillment of language preferences');
PARAMETER_TOOLTIPS.set('affiliation', 'Prefer exam-detail of staff members from module\'s unit');
PARAMETER_TOOLTIPS.set('location', 'Reward fulfillment of location preferences');
PARAMETER_TOOLTIPS.set('field', 'Reward fulfillment of field preferences');
PARAMETER_TOOLTIPS.set('holiday', 'Reward fulfillment of date exclusion preferences');
PARAMETER_TOOLTIPS.set('teaching_assistant', 'Prefer exam-detail of the module\'s TAs');
PARAMETER_TOOLTIPS.set('exam_phase', 'Prefer to allocate staff members in either regular or repetition exam phase');
PARAMETER_TOOLTIPS.set('violations', 'Minimize maximum number of preference violations among staff members');
PARAMETER_TOOLTIPS.set('workload', 'Minimize maximum relative deviation from contractual workload among staff members');
PARAMETER_TOOLTIPS.set('spread', 'Minimize maximum number of distinct exams to which staff members are allocated');

export const DEFAULT_PARAMETER_SET: ParameterSetRequest = {
  name: '',
  supervision: SUPERVISION_DEFAULT,
  correction: CORRECTION_DEFAULT
};
